import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { companyDetails } from "../data";
import SEO from "../components/SEO";

const defaultCourse = {
  course_id: "tag-ansvar-for-eget-lederskab",
  title: "Tag ansvar for eget lederskab – ud fra dine værdier",
  date: "Fastlægges snarest",
  start_time: "09.00",
  end_time: "16.00",
  location: "Odense",
  address_info:
    "Den præcise adresse og praktiske information modtager du i god tid før kursusdagen. Holdet afholdes i Odense centrum.",
  max_participants: 10,
};

export function UpcomingCoursesSection({ showHeader = true, id = "kommende-hold", isPage = false }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [registrationsCount, setRegistrationsCount] = useState({});
  const [activeCourse, setActiveCourse] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    website_url: "", // Honeypot antispam
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  useEffect(() => {
    fetchCoursesAndStatus();
  }, []);

  // Lock background scroll when modal is open and handle ESC key
  useEffect(() => {
    if (activeCourse) {
      const originalBodyOverflow = document.body.style.overflow;
      const originalDocOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      const handleKeyDown = (e) => {
        if (e.key === "Escape") setActiveCourse(null);
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalDocOverflow;
        window.removeEventListener("keydown", handleKeyDown);
      };
    }
  }, [activeCourse]);

  const fetchCoursesAndStatus = async () => {
    try {
      setLoading(true);

      // Fetch upcoming courses from CMS
      let fetchedCourses = [];
      try {
        const cmsRes = await fetch(
          "https://api.dev.auxo.dk/public/content/kommende_hold"
        );
        if (cmsRes.ok) {
          const cmsData = await cmsRes.json();
          // Support both array and { items: [...] } response structure
          const items = Array.isArray(cmsData)
            ? cmsData
            : cmsData && Array.isArray(cmsData.items)
            ? cmsData.items
            : [];
          if (items.length > 0) {
            fetchedCourses = items;
          }
        }
      } catch (err) {
        console.warn("Could not fetch CMS courses, using default fallback", err);
      }

      if (fetchedCourses.length === 0) {
        fetchedCourses = [defaultCourse];
      }

      setCourses(fetchedCourses);

      // Initialize status / registration counts for each course
      const counts = {};
      for (const course of fetchedCourses) {
        counts[course.course_id] = 0;
      }
      setRegistrationsCount(counts);
    } catch (error) {
      console.error("Error loading courses status:", error);
      setCourses([defaultCourse]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (course) => {
    setActiveCourse(course);
    setSubmitError("");
    setSubmitSuccess(null);
    setAcceptedTerms(false);
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      website_url: "",
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!activeCourse) return;

    // Client side validation
    if (!formData.first_name.trim()) {
      setSubmitError("Indtast venligst dit fornavn.");
      return;
    }
    if (!formData.last_name.trim()) {
      setSubmitError("Indtast venligst dit efternavn.");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setSubmitError("Indtast venligst en gyldig e-mailadresse.");
      return;
    }
    if (!formData.phone.trim()) {
      setSubmitError("Indtast venligst dit telefonnummer.");
      return;
    }
    if (!acceptedTerms) {
      setSubmitError(
        "Bekræft venligst, at du ønsker at modtage besked, når datoen for næste hold er fastlagt."
      );
      return;
    }

    setSubmitting(true);
    setSubmitError("");

    try {
      const response = await fetch(
        "https://api.dev.auxo.dk/webform/submit",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            subject: `Interesse for næste hold: ${activeCourse.title || "Tag ansvar for eget lederskab"}`,
            name: `${formData.first_name.trim()} ${formData.last_name.trim()}`,
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            email: formData.email.trim(),
            phone: formData.phone.trim(),
            course_id: activeCourse.course_id,
            course_title: activeCourse.title || "Tag ansvar for eget lederskab",
            course_dates: activeCourse.date || "Fastlægges snarest",
            course_location: activeCourse.location || "Odense",
            inquiry_type: "Interesseliste – besked om næste hold",
            website_url: formData.website_url, // honeypot
          }),
        }
      );

      const result = await response.json();

      if (response.ok && (result.status === "ok" || result.success)) {
        setSubmitSuccess({
          first_name: formData.first_name.trim(),
          course: activeCourse,
        });
        // Update local count
        setRegistrationsCount((prev) => ({
          ...prev,
          [activeCourse.course_id]: (prev[activeCourse.course_id] || 0) + 1,
        }));
      } else {
        setSubmitError(
          result.message ||
            result.error ||
            "Der opstod en fejl under tilmeldingen. Prøv venligst igen."
        );
      }
    } catch (err) {
      console.error("Registration submit error:", err);
      setSubmitError(
        "Der kunne ikke oprettes forbindelse til serveren. Tjek din internetforbindelse og prøv igen."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id={id} className="scroll-mt-24">
      {/* Header Section */}
      {showHeader && (
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <span className="inline-flex items-center px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider bg-maersk-blue/10 text-maersk-blue mb-4">
            <i className="fa-solid fa-bell mr-2" /> KOMMENDE HOLD · INTERESSELISTE
          </span>
          {isPage ? (
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 font-bold mb-6 leading-tight">
              Kommende lederforløb
            </h1>
          ) : (
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-navy-900 font-bold mb-6 leading-tight">
              Kommende lederforløb
            </h2>
          )}
          <p className="text-navy-700 text-base sm:text-lg leading-relaxed font-medium mb-3">
            Næste hold åbner snart – skriv dig på interesselisten og få besked som en af de første.
          </p>
          <p className="text-navy-600 text-sm sm:text-base leading-relaxed">
            Hvert hold følger et samlet 12-måneders lederforløb med 4 moduler – ét pr. kvartal – og maksimalt 10 deltagere for optimal fortrolighed og intensiv ledersparring.
          </p>
        </div>
      )}

      {/* Courses List */}
      {loading ? (
        <div className="text-center py-16 bg-white rounded-2xl shadow-sm border border-stone-200/60">
          <i className="fa-solid fa-spinner fa-spin text-3xl text-maersk-blue mb-4" />
          <p className="text-navy-700 font-medium">
            Henter kommende hold...
          </p>
        </div>
      ) : (
        <div className="space-y-8 max-w-4xl mx-auto">
          {courses.map((course) => {
            const count = registrationsCount[course.course_id] || 0;
            const maxCount = course.max_participants || 10;
            const isFull = count >= maxCount;
            const seatsLeft = Math.max(0, maxCount - count);

            return (
              <div
                key={course.course_id}
                className="bg-white rounded-2xl p-6 sm:p-8 border border-stone-200/80 shadow-md hover:shadow-lg transition-shadow duration-300 relative overflow-hidden text-left"
              >
                {/* Status badge */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6 border-b border-stone-100 pb-4">
                  <div className="inline-flex items-center text-sm font-semibold text-maersk-blue bg-maersk-blue/10 px-3 py-1 rounded-lg">
                    <i className="fa-solid fa-graduation-cap mr-2" />
                    Årsforløb · 4 moduler over 12 måneder
                  </div>

                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                    <i className="fa-solid fa-bell mr-1.5 text-amber-600" />
                    Interesseliste åben · Max. 10 deltagere
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl sm:text-3xl text-navy-900 font-bold mb-6 leading-snug">
                  {course.title}
                </h3>

                {/* Course Details Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 bg-stone-50 p-4 sm:p-5 rounded-xl border border-stone-200/50">
                  <div className="flex items-start space-x-3">
                    <i className="fa-solid fa-calendar text-maersk-blue text-lg mt-0.5" />
                    <div>
                      <span className="text-xs font-bold text-navy-500 uppercase tracking-wider block">
                        Dato
                      </span>
                      <span className="text-sm font-semibold text-navy-900">
                        {course.date}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <i className="fa-solid fa-clock text-maersk-blue text-lg mt-0.5" />
                    <div>
                      <span className="text-xs font-bold text-navy-500 uppercase tracking-wider block">
                        Tidspunkt
                      </span>
                      <span className="text-sm font-semibold text-navy-900">
                        {course.start_time}–{course.end_time}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3">
                    <i className="fa-solid fa-location-dot text-maersk-blue text-lg mt-0.5" />
                    <div>
                      <span className="text-xs font-bold text-navy-500 uppercase tracking-wider block">
                        Lokation
                      </span>
                      <span className="text-sm font-semibold text-navy-900">
                        {course.location}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Address info notice */}
                <div className="bg-blue-50/60 border border-blue-100 rounded-xl p-4 sm:p-5 mb-8 text-sm text-navy-800 flex items-start space-x-3">
                  <i className="fa-solid fa-bell text-maersk-blue text-base mt-0.5 shrink-0" />
                  <div className="leading-relaxed space-y-2">
                    <p className="font-semibold text-navy-900">
                      Næste hold åbner snart – skriv dig på interesselisten og få besked som en af de første.
                    </p>
                    <p>
                      Forløbet består af 4 kursusdage – én dag pr. kvartal over 12 måneder. Når datoen for næste hold er fastlagt, får du direkte besked og mulighed for at sikre dig en plads, inden holdet åbner for generel tilmelding.
                    </p>
                  </div>
                </div>

                {/* CTA button or status */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 pt-2 border-t border-stone-100">
                  <div className="text-xs text-stone-500 flex items-center">
                    <i className="fa-solid fa-envelope text-maersk-blue mr-1.5 text-sm" />
                    Uforpligtende opskrivning – få direkte besked ved datoåbning
                  </div>

                  <button
                    onClick={() => handleOpenForm(course)}
                    className="w-full sm:w-auto px-8 py-3.5 bg-maersk-blue hover:bg-navy-800 text-white font-bold rounded-xl transition-all duration-200 shadow-md hover:shadow-lg text-center text-sm sm:text-base flex items-center justify-center cursor-pointer"
                  >
                    <span>Få besked om næste hold</span>
                    <i className="fa-solid fa-arrow-right ml-2.5 text-sm" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal / Overlay for Registration Form */}
      {activeCourse &&
        createPortal(
          <div className="fixed inset-0 z-[99999]">
            {/* 1. Backdrop Layer */}
            <div
              className="fixed inset-0 modal-backdrop transition-opacity cursor-pointer z-0"
              onClick={() => setActiveCourse(null)}
              aria-hidden="true"
            />

            {/* 2. Centered Positioning Layer (isolated flexbox) */}
            <div className="fixed inset-0 z-10 flex items-center justify-center p-3.5 sm:p-6 pointer-events-none">
              {/* 3. Modal Card Panel */}
              <div
                className="pointer-events-auto relative w-full max-w-xl rounded-2xl bg-white text-left shadow-2xl border border-stone-100 flex flex-col overflow-hidden max-h-[calc(100dvh-1.75rem)] sm:max-h-[calc(100dvh-3rem)] min-w-0 box-border"
                onClick={(e) => e.stopPropagation()}
              >
                  {/* Close button */}
                  <button
                    onClick={() => setActiveCourse(null)}
                    className="absolute top-3 right-3 sm:top-5 sm:right-5 text-stone-400 hover:text-navy-900 transition-colors p-2 rounded-lg bg-stone-100 hover:bg-stone-200 text-sm font-bold z-20 cursor-pointer"
                    aria-label="Luk"
                  >
                    <i className="fa-solid fa-xmark text-base block w-4 h-4 text-center" />
                  </button>

                  <div className="overflow-y-auto overscroll-contain custom-scrollbar p-3.5 sm:p-8 space-y-4 min-w-0 flex-1">
                    {submitSuccess ? (
                /* Success view */
                <div className="text-center py-2 sm:py-4 space-y-4 sm:space-y-5">
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto text-xl sm:text-2xl">
                    <i className="fa-solid fa-check" />
                  </div>
                  <h3 className="font-serif text-xl sm:text-3xl font-bold text-navy-900">
                    Tak for din interesse, {submitSuccess.first_name}!
                  </h3>
                  <div className="bg-green-50/80 border border-green-200 rounded-xl p-4 sm:p-5 text-left text-navy-900 text-xs sm:text-sm leading-relaxed space-y-2">
                    <p className="font-semibold text-green-900">
                      Du er nu skrevet på interesselisten til:
                    </p>
                    <p className="font-medium">
                      &bull; {submitSuccess.course.title}
                    </p>
                    <p>&bull; Omfang: 4 kursusdage fordelt over 12 måneder</p>
                    <p>&bull; Sted: {submitSuccess.course.location || "Odense"}</p>
                    <p>&bull; Status: Fastsættelse af næste holdopstart er i gang</p>
                  </div>
                  <p className="text-stone-600 text-xs sm:text-sm leading-relaxed">
                    Så snart datoen for næste hold er fastlagt, modtager du direkte besked via e-mail og telefon, så du kan få en plads som en af de første.
                  </p>
                  <button
                    onClick={() => setActiveCourse(null)}
                    className="w-full py-3 sm:py-3.5 bg-navy-900 text-white font-bold rounded-xl hover:bg-navy-800 transition-colors text-sm mt-2 sm:mt-4 cursor-pointer"
                  >
                    Luk vindue
                  </button>
                </div>
              ) : (
                /* Form view */
                <div>
                  <div className="mb-4 sm:mb-6 pr-8 sm:pr-10">
                    <span className="text-[11px] sm:text-xs font-bold text-maersk-blue uppercase tracking-wider block mb-1">
                      Interesseliste
                    </span>
                    <h3 className="font-serif text-xl sm:text-2xl font-bold text-navy-900 leading-snug">
                      Få besked om næste hold
                    </h3>
                    <p className="text-stone-600 text-xs sm:text-sm mt-1">
                      Næste hold åbner snart – skriv dig på interesselisten og få besked som en af de første.
                    </p>
                    <div className="mt-2.5 sm:mt-3 p-3 sm:p-3.5 bg-stone-50 border border-stone-200/80 rounded-xl space-y-1 text-xs sm:text-sm text-navy-900">
                      <div className="font-bold text-navy-900">Tag ansvar for eget lederskab</div>
                      <div className="text-stone-700 text-xs sm:text-sm">Opstart: Fastlægges snarest · Odense</div>
                      <div className="text-maersk-blue font-semibold text-[11px] sm:text-xs uppercase tracking-wider pt-0.5">
                        4 kursusdage fordelt over 12 måneder · Max. 10 deltagere
                      </div>
                    </div>
                  </div>

                  {submitError && (
                    <div className="mb-4 p-3.5 sm:p-4 bg-red-50 border border-red-200 rounded-xl text-red-800 text-xs sm:text-sm flex items-start space-x-2.5">
                      <i className="fa-solid fa-circle-exclamation text-red-600 text-base mt-0.5 shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-3.5 sm:space-y-4">
                    {/* Honeypot anti-spam field (absolutely hidden) */}
                    <div className="sr-only absolute left-[-9999px]" aria-hidden="true">
                      <label htmlFor="website_url">Hjemmeside URL</label>
                      <input
                        type="text"
                        id="website_url"
                        name="website_url"
                        tabIndex="-1"
                        autoComplete="off"
                        value={formData.website_url}
                        onChange={(e) =>
                          setFormData({ ...formData, website_url: e.target.value })
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 min-w-0">
                      <div className="min-w-0">
                        <label htmlFor="course_first_name" className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-navy-800 mb-1">
                          Fornavn <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="course_first_name"
                          type="text"
                          required
                          value={formData.first_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              first_name: e.target.value,
                            })
                          }
                          placeholder="F.eks. Mette"
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-stone-300 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/20 outline-hidden text-sm transition-all min-w-0 box-border"
                        />
                      </div>

                      <div className="min-w-0">
                        <label htmlFor="course_last_name" className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-navy-800 mb-1">
                          Efternavn <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="course_last_name"
                          type="text"
                          required
                          value={formData.last_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              last_name: e.target.value,
                            })
                          }
                          placeholder="F.eks. Frederiksen"
                          className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-stone-300 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/20 outline-hidden text-sm transition-all min-w-0 box-border"
                        />
                      </div>
                    </div>

                    <div className="min-w-0">
                      <label htmlFor="course_email" className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-navy-800 mb-1">
                        E-mailadresse <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="course_email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        placeholder="din@email.dk"
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-stone-300 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/20 outline-hidden text-sm transition-all min-w-0 box-border"
                      />
                    </div>

                    <div className="min-w-0">
                      <label htmlFor="course_phone" className="block text-[11px] sm:text-xs font-bold uppercase tracking-wider text-navy-800 mb-1">
                        Telefonnummer <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="course_phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={(e) =>
                          setFormData({ ...formData, phone: e.target.value })
                        }
                        placeholder="+45 12 34 56 78"
                        className="w-full px-3 py-2 sm:px-4 sm:py-3 rounded-xl border border-stone-300 focus:border-maersk-blue focus:ring-2 focus:ring-maersk-blue/20 outline-hidden text-sm transition-all min-w-0 box-border"
                      />
                    </div>

                    {/* Price and Order Summary Box */}
                    <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 sm:p-5 text-xs sm:text-sm text-navy-900 space-y-2.5 sm:space-y-3 min-w-0">
                      <h4 className="font-serif text-sm sm:text-base font-bold text-navy-900 pb-2 border-b border-stone-200 flex flex-wrap items-center justify-between gap-1.5 min-w-0">
                        <span>Information om årsforløbet</span>
                        <span className="text-[11px] sm:text-xs font-sans font-semibold text-maersk-blue bg-maersk-blue/10 px-2.5 py-0.5 rounded-full shrink-0">
                          12 måneders forløb
                        </span>
                      </h4>
                      <div className="space-y-1.5 text-xs sm:text-sm min-w-0">
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2 min-w-0">
                          <span className="text-stone-600 shrink-0">Årsforløb:</span>
                          <span className="font-semibold text-navy-900 sm:text-right min-w-0 break-words">Tag ansvar for eget lederskab</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2 min-w-0">
                          <span className="text-stone-600 shrink-0">Omfang:</span>
                          <span className="font-medium text-navy-900 sm:text-right min-w-0 break-words">4 kursusdage over 12 måneder</span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:justify-between gap-0.5 sm:gap-2 min-w-0">
                          <span className="text-stone-600 shrink-0">Opstart:</span>
                          <span className="font-medium text-navy-900 sm:text-right min-w-0 break-words">Fastlægges snarest (Odense)</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-stone-200/80 font-semibold text-navy-900 min-w-0">
                          <span>Pris for forløbet:</span>
                          <span className="text-maersk-blue font-bold text-sm sm:text-base shrink-0">29.995,- kr. + moms</span>
                        </div>
                      </div>
                      <p className="text-[11px] sm:text-xs text-stone-600 pt-2 border-t border-stone-200/80 leading-relaxed min-w-0 break-words">
                        <strong>Inkluderet:</strong> Undervisning, materialer og fuld forplejning alle 4 kursusdage.
                      </p>
                    </div>

                    {/* Mandatory Acceptance Checkbox */}
                    <div className="pt-1 sm:pt-2 min-w-0">
                      <label className="flex items-start space-x-2.5 sm:space-x-3 cursor-pointer select-none bg-stone-50 p-2.5 sm:p-3.5 border border-stone-200 rounded-xl hover:border-stone-300 transition-colors min-w-0">
                        <input
                          type="checkbox"
                          required
                          checked={acceptedTerms}
                          onChange={(e) => setAcceptedTerms(e.target.checked)}
                          className="mt-0.5 h-4 w-4 shrink-0 rounded border-stone-300 text-maersk-blue focus:ring-maersk-blue cursor-pointer"
                        />
                        <span className="text-xs sm:text-sm text-navy-900 leading-snug break-words min-w-0">
                          Ja tak, jeg vil gerne skrives på interesselisten og få besked, når datoen for næste hold er fastlagt. (Uforpligtende) <span className="text-red-500 font-bold">*</span>
                        </span>
                      </label>
                    </div>

                    <div className="pt-1 sm:pt-2">
                      <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 sm:py-3.5 px-4 bg-maersk-blue hover:bg-navy-800 text-white font-bold rounded-xl transition-all duration-200 shadow-md text-sm sm:text-base flex items-center justify-center disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                      >
                        {submitting ? (
                          <>
                            <i className="fa-solid fa-spinner fa-spin mr-2" />
                            <span>Sender din forespørgsel...</span>
                          </>
                        ) : (
                          <>
                            <span>Få besked om næste hold</span>
                            <i className="fa-solid fa-bell ml-2 text-sm" />
                          </>
                        )}
                      </button>
                    </div>

                    <p className="text-[11px] sm:text-xs text-stone-500 text-center pt-0.5 leading-relaxed">
                      Opskrivningen er 100% uforpligtende. Du modtager udelukkende besked om datoer for næste hold og mulighed for reservation.
                    </p>
                  </form>
                </div>
              )}
                  </div>
                </div>
              </div>
            </div>,
          document.body
        )}
    </section>
  );
}

export default function UpcomingCourses() {
  const upcomingSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Course",
        "name": "Kommende hold – Tag ansvar for dit lederskab",
        "description": "Se datoer og tilmeld dig kommende hold i hele landet. 12-måneders lederforløb med 4 moduler og max. 10 deltagere.",
        "provider": {
          "@type": "Organization",
          "name": "Lederskab.nu",
          "url": "https://lederskab.nu"
        }
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://lederskab.nu/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Kommende hold",
            "item": "https://lederskab.nu/kommende-hold"
          }
        ]
      }
    ]
  };

  return (
    <div className="bg-stone-50 min-h-screen py-12 lg:py-20">
      <SEO
        title="Kommende Hold & Interesseliste | Lederforløb | Lederskab.nu"
        description="Næste hold åbner snart – skriv dig på interesselisten og få besked som en af de første. 12-måneders lederforløb i Odense med max. 10 deltagere."
        canonicalPath="/kommende-hold"
        structuredData={upcomingSchema}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        <UpcomingCoursesSection showHeader={true} isPage={true} />

        {/* Contact/Questions banner */}
        <div className="mt-16 bg-navy-900 text-white rounded-2xl p-8 sm:p-10 max-w-4xl mx-auto shadow-xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="font-serif text-xl sm:text-2xl font-bold">
              Har du spørgsmål til et kommende hold?
            </h3>
            <p className="text-stone-300 text-sm max-w-lg">
              Du er altid velkommen til at kontakte Anita direkte for uforpligtende afklaring.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full sm:w-auto">
            <a
              href={`tel:${companyDetails.phone}`}
              className="px-5 py-3 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl text-white text-sm font-semibold transition-colors flex items-center justify-center"
            >
              <i className="fa-solid fa-phone mr-2 text-xs" />
              {companyDetails.phone}
            </a>
            <a
              href={`mailto:${companyDetails.email}`}
              className="px-5 py-3 bg-maersk-blue hover:bg-navy-800 rounded-xl text-white text-sm font-bold transition-colors flex items-center justify-center"
            >
              <i className="fa-solid fa-envelope mr-2 text-xs" />
              {companyDetails.email}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
